<template>
  <div class="tw-font-geist">
    <div
      class="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-center sm:tw-px-2 tw-gap-3 md:tw-gap-0 tw-pt-2 tw-pb-5 tw-flex-wrap"
    >
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto"
      >
        <PodcastersDropdown @select-podcaster="selectPodcaster" />
        <ShowsDropdown
          :selected-podcaster="selectedPodcaster"
          @select-show="selectShow"
          :class="[selectedPodcaster === 'podcastAdmin' ? 'tw-hidden' : '']"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 tw-w-full sm:tw-w-auto tw-justify-end"
      >
        <Dropdown
          v-model="campaignType"
          :options="campaignTypeOptions"
          optionValue="value"
          optionLabel="label"
          placeholder="All Campaigns"
          class="tw-border tw-border-transparent tw-outline-none tw-shadow dark:tw-bg-slate-800 dark:tw-border-slate-700"
          input-class="tw-p-1 tw-pl-2 tw-text-sm tw-font-medium"
        >
          <template #value="slotProps">
            <div class="tw-flex tw-gap-2 tw-items-center">
              <i class="pi pi-megaphone tw-text-sm"></i>
              <span class="tw-capitalize">
                {{ slotProps.value ? slotProps.value : slotProps.placeholder }}
              </span>
            </div>
          </template>
        </Dropdown>
        <DateMenu @set-date-range="setDateRange" />
      </div>
    </div>
    <div class="tw-flex tw-gap-2 tw-flex-wrap">
      <OverviewCard
        v-for="overview in overviews"
        :icon="overview.icon"
        :title="overview.title"
        :value="overview.total"
        :second-value="overview.diff"
        :loading="loadingOverviews"
        :active-card="overview.type === overtimeEventType"
        :clickable="overview.clickable"
        :class="[loadingOverviews && 'tw-pointer-events-none']"
        @click="
          handleActiveOverviewCard(
            overview.type,
            overview.status,
            overview.clickable
          )
        "
      />
    </div>
    <div class="tw-mt-4">
      <OvertimeCard
        :data="overtimeData"
        :loading="loadingOvertimeData"
        :title="overtimeType"
      />
    </div>
    <div
      class="tw-mt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-items-stretch"
    >
      <TopCountriesCard
        :data="topByCountries"
        :loading="loadingTopByCountries"
        :type="type"
      />
      <ByDeviceCard :data="topByDevices" :loading="loadingTopByDevices" />
    </div>
    <div class="tw-mt-4">
      <ShowsTable
        :data="showsData"
        :loading="loadingShowsData"
        :selected-show="selectedShow"
        @refresh-data="fetchShowsData"
      />
    </div>
    <div class="tw-mt-4">
      <DailyCampaignTypeTable
        :data="dailyCampaignTypeData"
        :loading="loadingDailyCampaignTypeData"
        @refresh-data="fetchDailyCampaignType"
      />
    </div>
    <div class="tw-mt-4">
      <StatsTable
        :data="statsData"
        :loading="loadingStatsData"
        @refresh-data="fetchStatsData"
      />
    </div>
    <div class="tw-mt-4">
      <HostingMonthlyTable :token="token" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import PodcastersDropdown from "./components/PodcastersDropdown.vue";
import ShowsDropdown from "./components/ShowsDropdown.vue";
import Dropdown from "primevue/dropdown";
import DateMenu from "./components/DateMenu.vue";
import moment from "moment";
import { API_BASE_URL } from "@/consts";
import axios from "axios";
import OverviewCard from "./components/OverviewCard.vue";
import OvertimeCard from "./components/OvertimeCard.vue";
import TopCountriesCard from "./components/TopCountriesCard.vue";
import ByDeviceCard from "./components/ByDeviceCard.vue";
import ShowsTable from "./components/ShowsTable.vue";
import DailyCampaignTypeTable from "./components/DailyCampaignTypeTable.vue";
import StatsTable from "./components/StatsTable.vue";
import HostingMonthlyTable from "./components/HostingMonthlyTable.vue";

const overviewConfig = [
  {
    icon: "pi-eye",
    title: "Views",
    key: "total_views",
    type: "episode_imp",
    clickable: true,
    status: true,
  },
  {
    icon: "pi-play",
    title: "Sessions",
    key: "total_plays",
    type: "episode_play",
    clickable: true,
    status: true,
  },
  {
    icon: "pi-headphones",
    title: "Unique Listeners",
    key: "total_unique_listeners",
    type: "",
    clickable: false,
  },
  {
    icon: "pi-percentage",
    title: "CTR",
    key: "overall_ctr",
    type: "",
    clickable: false,
    isPercent: true,
  },
  {
    icon: "pi-bullseye",
    title: "Impressions",
    key: "impressions",
    type: "impressions",
    clickable: true,
  },
  {
    icon: "pi-file-excel",
    title: "ECPM",
    key: "ecpm",
    type: "",
    clickable: false,
    isFixed: 2,
  },
  {
    icon: "pi-wallet",
    title: "Cost",
    key: "revenue",
    type: "revenue",
    clickable: false,
    isCurrency: true,
  },
  {
    icon: "pi-dollar",
    title: "Revenue",
    key: "total_revenue",
    type: "total_revenue",
    clickable: true,
    isCurrency: true,
  },
];

const selectedPodcaster = ref("podcastAdmin");
const selectedShow = ref("all");
const campaignType = ref(null);
const dates = ref([new Date(moment().subtract(1, "week")), new Date(moment())]);
const loadingOverviews = ref(false);
const loadingOvertimeData = ref(false);
const eventType = ref("episode_imp");
const overtimeEventType = ref("episode_imp");
const overviews = ref(overviewConfig);
const overtimeData = ref([]);
const loadingTopByCountries = ref(false);
const topByCountries = ref([]);
const loadingTopByDevices = ref(false);
const topByDevices = ref([]);
const loadingShowsData = ref(false);
const showsData = ref([]);
const loadingDailyCampaignTypeData = ref(false);
const dailyCampaignTypeData = ref([]);
const loadingStatsData = ref(false);
const statsData = ref([]);

const token = localStorage.getItem("podcastAdminToken");
const campaignTypeOptions = [
  { label: "All", value: null },
  { label: "Publisher", value: "publisher" },
  { label: "Promotion", value: "promotion" },
];

const type = computed(() => {
  if (eventType.value === "episode_imp") {
    return "Reach";
  } else if (eventType.value === "episode_play") {
    return "Sessions";
  }
});

const overtimeType = computed(() => {
  const keyMap = {
    episode_imp: "Reach",
    episode_play: "Sessions",
    impressions: "Impressions",
    revenue: "Revenue",
  };
  return keyMap[overtimeEventType.value] || "Reach";
});

const handleActiveOverviewCard = (type, status, clickable) => {
  if (!clickable) return; // If the card is not clickable, do nothing
  overtimeEventType.value = type;
  if (status && eventType.value !== type) {
    eventType.value = type;
    fetchTopCountries();
    fetchTopDevices();
  }
};

const selectPodcaster = (podcaster) => {
  console.log("Selected podcaster: ", podcaster);
  selectedPodcaster.value = podcaster;
  selectedShow.value = "all";

  fetchOverviews();
  fetchOvertime();
  fetchTopCountries();
  fetchTopDevices();
  fetchShowsData();
  fetchDailyCampaignType();
  fetchStatsData();
};

const selectShow = (show) => {
  console.log("Selected show: ", show);
  selectedShow.value = show;

  fetchOverviews();
  fetchOvertime();
  fetchTopCountries();
  fetchTopDevices();
  fetchShowsData();
  fetchDailyCampaignType();
  fetchStatsData();
};

const setDateRange = (dateRange) => {
  dates.value = dateRange;
};

const generateDiff = (current, past) => {
  const currNum = parseFloat(current);
  const pastNum = parseFloat(past);
  if (isNaN(currNum) || isNaN(pastNum) || pastNum === 0) return "N/A"; // Avoid division by zero or invalid numbers
  const diff = ((currNum - pastNum) / pastNum) * 100;
  const sign = diff > 0 ? "+" : "";
  return `${sign}${diff.toFixed(2)}%`;
};

const formatNumber = (num) => {
  const value = Number(num);
  if (isNaN(value)) return num;
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
  if (value >= 1_000) return (value / 1_000).toFixed(1) + "K";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fetchOverviews = async () => {
  try {
    loadingOverviews.value = true;
    const { data } = await axios.get(API_BASE_URL + "/podcast_events/totals", {
      params: {
        podcasterId: selectedPodcaster.value,
        showId: selectedShow.value,
        startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
        endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
        campaignType: campaignType.value,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (data) {
      overviews.value = overviewConfig.map((cfg) => {
        let total;
        if (cfg.isPercent) {
          total = data[cfg.key] ? `${(data[cfg.key] * 100).toFixed(1)}%` : `0%`;
        } else if (cfg.isFixed) {
          total = data[cfg.key] ? `${data[cfg.key].toFixed(cfg.isFixed)}` : `0`;
        } else if (cfg.isCurrency) {
          total = `$${formatNumber(data[cfg.key])}`;
        } else {
          total = formatNumber(data[cfg.key]);
        }
        const past = data.pastTotals?.[cfg.key];
        return {
          icon: cfg.icon,
          title: cfg.title,
          total,
          type: cfg.type,
          clickable: cfg.clickable,
          status: cfg.status,
          diff: generateDiff(data[cfg.key], past) || "N/A",
        };
      });
    }
  } catch (error) {
    console.error("Error fetching shows:", error);
  } finally {
    setTimeout(() => {
      loadingOverviews.value = false;
    }, 300);
  }
};

const fetchOvertime = async () => {
  try {
    loadingOvertimeData.value = true;
    const { data } = await axios.get(
      API_BASE_URL + "/podcast_events/over-time",
      {
        params: {
          podcasterId: selectedPodcaster.value,
          showId: selectedShow.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          campaignType: campaignType.value,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (data) {
      overtimeData.value = data.over_time_data;
    }
  } catch (error) {
    console.error("Error fetching shows:", error);
  } finally {
    setTimeout(() => {
      loadingOvertimeData.value = false;
    }, 300);
  }
};

const fetchTopCountries = async () => {
  try {
    loadingTopByCountries.value = true;
    const { data } = await axios.get(
      `${API_BASE_URL}/podcast_events/top-by-countries`,
      {
        params: {
          podcasterId: selectedPodcaster.value,
          showId: selectedShow.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          eventTypes: eventType.value,
          campaignType: campaignType.value,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (data) {
      topByCountries.value = data.top_by_countries;
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingTopByCountries.value = false;
    }, 300);
  }
};

const fetchTopDevices = async () => {
  try {
    loadingTopByDevices.value = true;
    const { data } = await axios.get(
      `${API_BASE_URL}/podcast_events/by-device-type`,
      {
        params: {
          podcasterId: selectedPodcaster.value,
          showId: selectedShow.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
          eventTypes: eventType.value,
          campaignType: campaignType.value,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (data) {
      topByDevices.value = data.by_device_type;
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingTopByDevices.value = false;
    }, 300);
  }
};

const fetchShowsData = async () => {
  try {
    loadingShowsData.value = true;
    if (selectedShow.value === "all") {
      const { data } = await axios.get(
        `${API_BASE_URL}/podcast_events/shows-data`,
        {
          params: {
            podcasterId: selectedPodcaster.value,
            startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
            endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
            campaignType: campaignType.value,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data) {
        showsData.value = data.shows_data;
      }
    } else {
      const { data } = await axios.get(
        `${API_BASE_URL}/podcast_events/episode-data`,
        {
          params: {
            podcasterId: selectedPodcaster.value,
            showId: selectedShow.value,
            startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
            endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
            campaignType: campaignType.value,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data) {
        showsData.value = data.episode_data;
      }
    }
  } catch (err) {
    console.log(err.message);
  } finally {
    setTimeout(() => {
      loadingShowsData.value = false;
    }, 300);
  }
};

const fetchDailyCampaignType = async () => {
  try {
    loadingDailyCampaignTypeData.value = true;
    const { data } = await axios.get(
      API_BASE_URL + "/podcast_events/campaign-type-daily",
      {
        params: {
          podcasterId: selectedPodcaster.value,
          showId: selectedShow.value,
          startDate: moment(dates.value[0]).format("YYYY-MM-DD"),
          endDate: moment(dates.value[1]).format("YYYY-MM-DD"),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (data) {
      dailyCampaignTypeData.value = data.campaign_type_daily_data;
    }
  } catch (error) {
    console.error("Error fetching daily campaign type:", error);
  } finally {
    setTimeout(() => {
      loadingDailyCampaignTypeData.value = false;
    }, 300);
  }
};

const fetchStatsData = async () => {
  try {
    loadingStatsData.value = true;

    const response = await axios.post(
      `${API_BASE_URL}/podcast-admin/stats-report/run-report`,
      {
        dateRange: {
          start: moment(dates.value[0]).format("YYYY-MM-DD"),
          end: moment(dates.value[1]).format("YYYY-MM-DD"),
        },
        shows: selectedShow.value === "all" ? [] : [selectedShow.value],
        podcasters:
          selectedPodcaster.value === "podcastAdmin"
            ? []
            : [selectedPodcaster.value],
        exclude_shows: [],
        exclude_podcasters: [],
        dimensions: ["Date"],
        metrics: [
          "Downloads",
          "Impressions",
          "Imp/Download Ratio",
          "Gross Profit",
          "Rev/Download Ratio",
          "Profit Margin",
          "ECPM",
        ],
        compareUntilNow: false,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    statsData.value = response.data?.tableData || [];
  } catch (error) {
    console.error("Error fetching stats data:", error);
  } finally {
    setTimeout(() => {
      loadingStatsData.value = false;
    }, 300);
  }
};

watch([dates], () => {
  if (dates.value && dates.value[0] !== null && dates.value[1] !== null) {
    fetchOverviews();
    fetchOvertime();
    fetchTopCountries();
    fetchTopDevices();
    fetchShowsData();
    fetchDailyCampaignType();
    fetchStatsData();
  }
});
watch([campaignType], () => {
  if (dates.value && dates.value[0] !== null && dates.value[1] !== null) {
    fetchOverviews();
    fetchOvertime();
    fetchTopCountries();
    fetchTopDevices();
    fetchShowsData();
  }
});
</script>
